// src/pages/OrdersList.js
import React, { useState, useEffect, useContext } from 'react';
import { Typography, Paper, Button, Box, CircularProgress, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TanStackTable from '../components/TanStackTable';
import { getOrders, updateOrderStatus as updateOrderStatusAPI, deleteMultipleOrders } from '../services/orderService';
import CurrencyFormatter from '../utils/CurrencyFormatter';
import { OrgContext } from '../contexts/OrgContext'; // Import OrgContext
import ButtonCellRenderer from '../components/ButtonCellRenderer'; // Import ButtonCellRenderer
import Papa from 'papaparse';
import BulkActionResults from '../components/BulkActionResults';
import { CurrencyContext } from '../contexts/CurrencyContext';
import formatDateToStandard, { DATE_FORMAT_DISPLAY } from '../utils/DateFormatter';

const OrdersList = () => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [deleteResults, setDeleteResults] = useState(null);
  const navigate = useNavigate();
  const { orgId } = useContext(OrgContext); // Access orgId from OrgContext
  const { currency } = useContext(CurrencyContext);

  const currencySymbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    AUD: 'A$',
    CAD: 'C$',
    CHF: 'CHF',
    CNY: '¥',
    INR: '₹',
    JPY: '¥',
    BDT: '৳',
  };

  const symbol = currencySymbols[currency] || currency;

  // Pagination and filtering state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('order_date');
  const [sortOrder, setSortOrder] = useState('DESC');

  // Memoize fetchOrders to prevent infinite loop
  const fetchOrders = React.useCallback(async (options = {}) => {
    setLoading(true);
    try {
      const response = await getOrders({
        page: options.page || pagination.page,
        limit: options.limit || pagination.limit,
        search: options.search !== undefined ? options.search : searchQuery,
        sortBy: options.sortBy || sortBy,
        sortOrder: options.sortOrder || sortOrder
      });
      
      let transformedData;
      let paginationData;
      
      // Check if response is paginated
      if (response.data && Array.isArray(response.data)) {
        transformedData = response.data.map(order => ({
          ...order,
          order_id: order.id,
          order_date: formatDateToStandard(order.order_date),
        }));
        paginationData = response.pagination;
      } else if (Array.isArray(response)) {
        // Fallback for non-paginated response
        transformedData = response.map(order => ({
          ...order,
          order_id: order.id,
          order_date: formatDateToStandard(order.order_date),
        }));
      }
      
      setRowData(transformedData);
      if (paginationData) {
        setPagination(paginationData);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    if (orgId) {
      fetchOrders();
    }
  }, [fetchOrders, orgId]);

  const calculateAmountWithTax = (order) => {
    // Always add tax, regardless of order type
    if (!order.meta?.tax_amount || !parseFloat(order.meta.tax_amount)) {
      return parseFloat(order.amount || 0);
    }
    
    const amount = parseFloat(order.amount || 0);
    const taxAmount = parseFloat(order.meta.tax_amount);
    return amount + taxAmount; // Always add tax amount
  };

  const columns = [
    {
      accessorKey: 'id',
      header: 'Order ID',
    },
    {
      accessorKey: 'order_date',
      header: `Date (${DATE_FORMAT_DISPLAY})`,
    },
    {
      accessorKey: 'order_type',
      header: 'Type',
    },
    {
      accessorKey: 'amount',
      header: 'Amount (Before Tax)',
      cell: ({ getValue }) => <CurrencyFormatter value={getValue()} />,
    },
    {
      accessorKey: 'tax_percentage',
      header: 'Tax %',
      accessorFn: (row) => {
        const value = row.meta?.tax_percentage;
        return (value && parseFloat(value)) ? parseFloat(value) : '--';
      },
      cell: ({ getValue }) => {
        const value = getValue();
        return value === '--' ? value : `${value}%`;
      },
    },
    {
      accessorKey: 'tax_amount',
      header: 'Tax Amount',
      accessorFn: (row) => {
        const value = row.meta?.tax_amount;
        return (value && parseFloat(value)) ? parseFloat(value) : '--';
      },
      cell: ({ getValue }) => {
        const value = getValue();
        return value === '--' ? value : <CurrencyFormatter value={value} />;
      },
    },
    {
      accessorKey: 'finalAmount',
      header: 'Final Amount',
      accessorFn: (row) => calculateAmountWithTax(row),
      cell: ({ getValue }) => <CurrencyFormatter value={getValue()} />,
    },
    {
      accessorKey: 'order_status',
      header: 'Status',
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <ButtonCellRenderer 
            data={row.original} 
            context={{ updateOrderStatus }} 
          />
        </div>
      ),
    },
  ];

  const handleRowClick = (row) => {
    // Navigate to order detail
    navigate(`/${orgId}/orders/${row.id}`);
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await updateOrderStatusAPI(orderId, status);
      setRowData((prevData) =>
        prevData.map((order) =>
          order.id === orderId ? { ...order, order_status: status } : order
        )
      );
    } catch (error) {
      console.error('Failed to update order status:', error);
    }
  };

  const handleDownload = () => {
    const exportData = rowData.map(order => ({
      'Order ID': order.id,
      'Date': formatDateToStandard(order.order_date),
      'Type': order.order_type,
      'Amount (Before Tax)': order.amount,
      'Tax Percentage': order.meta?.tax_percentage ? `${order.meta.tax_percentage}%` : '--',
      'Tax Amount': order.meta?.tax_amount ? `${symbol}${parseFloat(order.meta.tax_amount).toFixed(2)}` : '--',
      'Final Amount': calculateAmountWithTax(order),
      'Status': order.order_status,
      'Customer ID': order.customer_id,
      'Supplier ID': order.supplier_id,
      'Payment Status': order.payment_status,
      'Delivery Date': order.delivery_date ? formatDateToStandard(order.delivery_date) : 'N/A'
    }));

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const now = new Date();
    const isoDate = now.toISOString().slice(0, 10); // yyyy-mm-dd format
    const uniqueId = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}${String(now.getMilliseconds()).padStart(3, '0')}`;
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${isoDate}_orders_${uniqueId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteClick = () => {
    setDeleteMode(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await deleteMultipleOrders(selectedOrders);
      setDeleteResults(response);
      await fetchOrders(); // Now fetchOrders is defined
    } catch (error) {
      console.error('Error deleting orders:', error);
    } finally {
      setDeleteMode(false);
      setSelectedOrders([]);
    }
  };

  const handleCancelDelete = () => {
    setDeleteMode(false);
    setSelectedOrders([]);
    setDeleteResults(null);
  };

  const handleCloseResults = () => {
    setDeleteResults(null);
  };

  return (
    <Paper sx={{
      p: 2,
      bgcolor: 'background.paper',
      color: 'text.primary',
      backdropFilter: 'saturate(140%) blur(6px)',
      border: '1px solid',
      borderColor: 'divider',
    }}>
      <Typography variant="h5" gutterBottom>
        Orders
      </Typography>

      {deleteResults && (
        <BulkActionResults 
          results={deleteResults} 
          actionType="deletion"
          onClose={handleCloseResults}
        />
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1, alignItems: 'center' }}>
        {!deleteMode ? (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/${orgId}/orders/add`)}
            >
              Add Order
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate(`/${orgId}/orders/quick-sale`)}
            >
              Quick Sale
            </Button>
            <Button 
              variant="contained" 
              onClick={handleDownload}
            >
              Download Orders List
            </Button>
            <Button 
              variant="contained" 
              color="error" 
              onClick={handleDeleteClick}
            >
              Delete Orders
            </Button>
          </>
        ) : (
          <>
            <Button 
              variant="contained" 
              color="error" 
              onClick={handleConfirmDelete}
            >
              Confirm Delete
            </Button>
            <Button 
              variant="contained" 
              onClick={handleCancelDelete}
            >
              Cancel
            </Button>
          </>
        )}
      </Box>
      
      {!deleteMode && (
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 2, 
          mb: 2, 
          alignItems: 'center',
          '& .MuiButton-root': {
            height: 32,
            minWidth: 100,
            fontSize: '0.75rem',
            py: 0.4,
            px: 1.2,
          }
        }}>
          <Button
            variant="contained"
            onClick={() => {
              navigate(`/${orgId}/orders/add`, {
                state: {
                  order_type: 'Sale',
                  order_status: 'Completed',
                  order_date: new Date().toISOString().slice(0, 10),
                  delivery_date: new Date().toISOString().slice(0, 10)
                }
              });
            }}
            sx={{
              backgroundColor: '#43a047', // green
              color: '#fff',
              '&:hover': { backgroundColor: '#388e3c' },
            }}
          >
            Add Sale Order
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              navigate(`/${orgId}/orders/add`, {
                state: {
                  order_type: 'Purchase',
                  order_status: 'Completed',
                  order_date: new Date().toISOString().slice(0, 10),
                  delivery_date: new Date().toISOString().slice(0, 10)
                }
              });
            }}
            sx={{
              backgroundColor: '#ff9800', // orange
              color: '#fff',
              '&:hover': { backgroundColor: '#f57c00' },
            }}
          >
            Add Purchase Order
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              navigate(`/${orgId}/orders/pos`, {
                state: {
                  order_type: 'Sale',
                  order_status: 'Completed',
                  order_date: new Date().toISOString().slice(0, 10),
                  delivery_date: new Date().toISOString().slice(0, 10)
                }
              });
            }}
            sx={{
              backgroundColor: '#2196f3', // blue
              color: '#fff',
              '&:hover': { backgroundColor: '#1976d2' },
            }}
          >
            Add POS Order
          </Button>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {!deleteMode && (
            <TanStackTable
              data={rowData}
              columns={columns}
              onRowClick={handleRowClick}
              enablePagination={true}
              pagination={pagination}
              onPaginationChange={(newPage, newLimit) => {
                console.log('Orders pagination change requested:', newPage, newLimit);
                setPagination(prev => ({...prev, page: newPage, limit: newLimit}));
                fetchOrders({page: newPage, limit: newLimit});
              }}
              searchQuery={searchQuery}
              onSearchChange={(search) => {
                setSearchQuery(search);
                setPagination(prev => ({...prev, page: 1}));
                fetchOrders({search, page: 1});
              }}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={(sortBy, sortOrder) => {
                setSortBy(sortBy);
                setSortOrder(sortOrder);
                fetchOrders({sortBy, sortOrder});
              }}
            />
          )}
          {deleteMode && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Select</TableCell>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>
                    <Tooltip title={`Format: ${DATE_FORMAT_DISPLAY}`}>
                      <span>Date ({DATE_FORMAT_DISPLAY})</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rowData.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedOrders.includes(order.id)}
                        onChange={() => {
                          setSelectedOrders((prev) =>
                            prev.includes(order.id)
                              ? prev.filter((id) => id !== order.id)
                              : [...prev, order.id]
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell>{order.id}</TableCell>
                    <TableCell>{order.order_type}</TableCell>
                    <TableCell>{order.order_status}</TableCell>
                    <TableCell><CurrencyFormatter value={order.amount} /></TableCell>
                    <TableCell>
                      <Tooltip title={`Format: ${DATE_FORMAT_DISPLAY}`}>
                        <span>{formatDateToStandard(order.order_date)}</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </Paper>
  );
};

export default OrdersList;
